import { Node, mergeAttributes } from '@tiptap/core';
import { ReactNodeViewRenderer } from '@tiptap/react';
import MermaidNodeView from '../components/MermaidNodeView';

/**
 * TipTap node extension for embedding editable Mermaid diagrams.
 *
 * Stores the Mermaid source in a `code` attribute and renders via
 * {@link MermaidNodeView}. The node is a block-level atom (not directly
 * content-editable) — editing happens through the React component overlay.
 *
 * @example
 * editor.commands.insertMermaidBlock({ code: 'graph TD\n  A-->B' });
 */
const MermaidBlock = Node.create({
  name: 'mermaidBlock',
  group: 'block',
  atom: true,
  draggable: true,
  selectable: true,

  addAttributes() {
    return {
      code: {
        default: 'graph TD\n  A[Start] --> B[End]',
        parseHTML: (el) => el.getAttribute('data-mermaid-code') || el.textContent,
        renderHTML: (attrs) => ({ 'data-mermaid-code': attrs.code }),
      },
    };
  },

  parseHTML() {
    return [{ tag: 'div[data-mermaid-block]' }];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'div',
      mergeAttributes(HTMLAttributes, { 'data-mermaid-block': 'true' }),
      HTMLAttributes['data-mermaid-code'] || '',
    ];
  },

  addNodeView() {
    return ReactNodeViewRenderer(MermaidNodeView);
  },

  addCommands() {
    return {
      /**
       * Insert a Mermaid diagram block at the current cursor position.
       * @param {Object} attrs
       * @param {string} attrs.code - Mermaid source code.
       * @returns {boolean} Whether the command succeeded.
       */
      insertMermaidBlock:
        (attrs = {}) =>
        ({ chain }) =>
          chain()
            .insertContent({ type: this.name, attrs })
            .run(),
    };
  },
});

export default MermaidBlock;
